import './styles.scss'
import Select from '../../../Form/Select'
import { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { SHAPE_COMPONENT, SHAPE_FACTSHEET_BLUEPRINT } from '../../shapes'
import { getAttributeRequired, getTrackingFactLabel } from '../../helper'
import { useSelector } from 'react-redux'
import { FACT_ATTRIBUTE_IDS, FACT_IDS } from '../../constants'
import PricingResetConfirmationOverlay from '../../PricingResetConfirmationOverlay'
import activityTrackingService from '../../../../../services/activityTrackingService'
import { trackingEvent } from '../../../../../constants'

/**
 * Value is assumed to be required for the currency code attribute
 */
const AttributeCurrencySelector = ({
                                     blueprint,
                                     parentBlueprint,
                                     factsheetBlueprint,
                                     factInstanceKey,
                                     data,
                                     collapsed,
                                     putAttribute,
                                     deleteAttribute,
                                     putValidationStatus,
                                   }) => {
  const [isValid, setIsValid] = useState(true)
  const [showCurrencyRemovalConfirmationDialog, setShowCurrencyRemovalConfirmationDialog] = useState(false)
  const [currencyCodeToBeConfirmed, setCurrencyCodeToBeConfirmed] = useState('')
  const currencyFact = useSelector(state => state.factsheet.facts[parentBlueprint.id])
  const countryFact = useSelector(state => state.factsheet.facts[FACT_IDS.COUNTRY])
  const countryCode = useMemo(() => {
    if (!countryFact) {
      return null
    }
    return countryFact[0]?.attributes[FACT_ATTRIBUTE_IDS.COUNTRY_CODE]?.value
  }, [countryFact])
  const currencies = useMemo(() => {
    const currencies = [...(blueprint.currencyMap[countryCode] ?? [])]
    currencies.unshift({
      code: '',
      label: 'Select currency',
    })
    return currencies
  }, [blueprint.currencyMap, countryCode])

  const factDefId = parentBlueprint.id
  const fact = factsheetBlueprint.factTree.facts[factDefId]
  const attributeDefId = blueprint.id
  const attribute = factsheetBlueprint.factTree.attributes[attributeDefId]
  const required = useMemo(
    () => getAttributeRequired(blueprint, attribute, fact),
    [
      blueprint,
      attribute,
      fact,
    ]
  )

  const trackingEventOpts = useMemo(() => {
    return { name: getTrackingFactLabel(parentBlueprint, factsheetBlueprint) }
  }, [parentBlueprint, factsheetBlueprint])

  const validateValue = useCallback((value) => {
    if (required.attribute && value === '') {
      return false
    }
    return currencies.some(c => c.code === value)
  }, [currencies, required])

  const handleCurrencyValueChange = useCallback((newValue) => {
    activityTrackingService.trackEvent(trackingEvent.editValue, trackingEventOpts)
    if (newValue === '') {
      deleteAttribute(parentBlueprint.id, factInstanceKey, blueprint.id)
      return
    }
    putAttribute(parentBlueprint.id, factInstanceKey, blueprint.id, {
      attributeDefId: blueprint.id,
      value: newValue,
    })
  }, [
    deleteAttribute,
    putAttribute,
    blueprint.id,
    parentBlueprint.id,
    factInstanceKey,
    trackingEventOpts,
  ])

  const onValueChange = useCallback(e => {
    if (data?.value && e.target.value !== data.value) {
      setShowCurrencyRemovalConfirmationDialog(true)
      setCurrencyCodeToBeConfirmed(e.target.value)
      return
    }
    handleCurrencyValueChange(e.target.value)
  }, [handleCurrencyValueChange, data])

  const onCancelPricingReset = useCallback(() => {
    setShowCurrencyRemovalConfirmationDialog(false)
    setCurrencyCodeToBeConfirmed('')
  }, [])

  const onConfirmPricingReset = useCallback(() => {
    setShowCurrencyRemovalConfirmationDialog(false)
    handleCurrencyValueChange(currencyCodeToBeConfirmed)
  }, [handleCurrencyValueChange, currencyCodeToBeConfirmed])

  useEffect(() => {
    if (!currencyFact) {
      setIsValid(true)
      return
    }
    const validationStatus = validateValue(data?.value ?? '')
    setIsValid(validationStatus)
    putValidationStatus(parentBlueprint.id, factInstanceKey, blueprint.id, validationStatus)
  }, [
    validateValue,
    data,
    currencyFact,
    putValidationStatus,
    parentBlueprint.id,
    factInstanceKey,
    blueprint.id,
  ])

  if (collapsed) {
    return null
  }
  return (
    <>
      {showCurrencyRemovalConfirmationDialog && <PricingResetConfirmationOverlay
        onCancel={onCancelPricingReset}
        onConfirm={onConfirmPricingReset}
        type="currency"
      />}
      <Select
        valid={isValid}
        selected={data?.value ?? ''}
        onChange={onValueChange}
        options={currencies}
        optionLabelKey="label"
        optionValueKey="code"
      />
    </>
  )
}

AttributeCurrencySelector.propTypes = {
  blueprint: PropTypes.shape(SHAPE_COMPONENT).isRequired,
  parentBlueprint: PropTypes.shape(SHAPE_COMPONENT).isRequired,
  factsheetBlueprint: PropTypes.shape(SHAPE_FACTSHEET_BLUEPRINT).isRequired,
  factInstanceKey: PropTypes.number.isRequired,
  data: PropTypes.object,
  collapsed: PropTypes.bool.isRequired,
  putAttribute: PropTypes.func.isRequired,
  deleteAttribute: PropTypes.func.isRequired,
  putValidationStatus: PropTypes.func.isRequired,
}

export default AttributeCurrencySelector
